import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import "./HeroSectionOnboarding.css";

const HeroSectionOnboarding = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoginForm, setIsLoginForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [signupStep, setSignupStep] = useState(false);
  const [signupOtp, setSignupOtp] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    employees: "",
    password: "",
  });

  const [loginStep, setLoginStep] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", otp: "" });

  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  // Animation state
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
  }, []);

  // ---------------------- Utils ----------------------
  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleLoginInputChange = (e) =>
    setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  // ---------------------- Signup Flow ----------------------
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      if (!signupStep) {
        const res = await fetch(`${API_BASE_URL}/api/auth/signup-send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to send OTP");

        setSignupStep(true);
        startResendTimer();
        showMessage("success", "OTP sent to your email. Please verify.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/auth/signup-verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, otp: signupOtp }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Invalid OTP");

        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("companyId", data.companyId);
        sessionStorage.setItem("companyName", formData.company);
        sessionStorage.setItem("userName", formData.name);

        navigate("/dashboard");
      }
    } catch (err) {
      showMessage("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------- Login Flow ----------------------
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      if (!loginStep) {
        const res = await fetch(`${API_BASE_URL}/api/auth/login-send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: loginData.email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to send OTP");

        setLoginStep(true);
        startResendTimer();
        showMessage("success", "OTP sent to your email.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/auth/login-verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginData),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Invalid OTP");

        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("companyId", data.companyId);
        sessionStorage.setItem("companyName", data.companyName);
        sessionStorage.setItem("userName", data.userName);

        navigate("/dashboard");
      }
    } catch (err) {
      showMessage("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------- Resend OTP ----------------------
  const handleResendOtp = async (type) => {
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      const endpoint = `${API_BASE_URL}/api/auth/resend-otp`;
      const body = type === "signup" ? { email: formData.email } : { email: loginData.email };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to resend OTP");

      startResendTimer();
      showMessage("success", "New OTP sent!");
    } catch (err) {
      showMessage("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------- Google Login ----------------------
  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/api/google-auth/google`;
  };

  return (
    <div className="quantum-onboard-container">
      {/* Background Animation */}
      <div className="quantum-background-canvas">
        <div className="quantum-orb orb-primary"></div>
        <div className="quantum-orb orb-secondary"></div>
        <div className="quantum-orb orb-tertiary"></div>
        <div className="quantum-particle particle-alpha"></div>
        <div className="quantum-particle particle-beta"></div>
        <div className="quantum-particle particle-gamma"></div>
      </div>

      <div className="quantum-content-wrapper">
        <div className={`quantum-text-panel ${animate ? "quantum-fade-in" : ""}`}>
          <h1 className="quantum-main-headline">
            Streamline Your{" "}
            <span className="quantum-gradient-accent">Business Operations</span>
          </h1>
          <p className="quantum-subtitle">
            Our all-in-one platform helps teams of all sizes collaborate efficiently, 
            manage projects seamlessly, and boost productivity with intelligent workflows.
          </p>
          
          <div className="quantum-features-grid">
            <div className="quantum-feature-card">
              <div className="quantum-feature-icon">🚀</div>
              <span>Project Management</span>
            </div>
            <div className="quantum-feature-card">
              <div className="quantum-feature-icon">🤝</div>
              <span>Team Collaboration</span>
            </div>
            <div className="quantum-feature-card">
              <div className="quantum-feature-icon">📊</div>
              <span>Advanced Analytics</span>
            </div>
          </div>
          
          <div className="quantum-cta-section">
            <button
              className="quantum-btn quantum-btn-primary quantum-pulse"
              onClick={() => {
                setIsModalOpen(true);
                setIsLoginForm(false);
                setSignupStep(false);
              }}
            >
              Get Started Free
            </button>
            <button
              className="quantum-btn quantum-btn-secondary"
              onClick={() => {
                setIsModalOpen(true);
                setIsLoginForm(true);
                setLoginStep(false);
              }}
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Visual Panel */}
      <div className={`quantum-visual-panel ${animate ? "quantum-slide-in" : ""}`}>
  <div className="quantum-visual-container">
    
    {/* Dashboard Widget with Image */}
    <div className="quantum-floating-widget widget-dashboard">
      <div className="widget-image-container">
        <img 
          src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=200&q=80" 
          alt="Dashboard Overview" 
          className="widget-image"
        />
        <div className="widget-overlay"></div>
      </div>
      <div className="widget-content">
        <div className="widget-header">
          <div className="widget-controls">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <div className="widget-progress">
          <div className="progress-indicator"></div>
        </div>
        <div className="widget-items">
          <div className="widget-item"></div>
          <div className="widget-item"></div>
          <div className="widget-item"></div>
        </div>
      </div>
    </div>

    {/* Analytics Widget with Image */}
    <div className="quantum-floating-widget widget-analytics">
      <div className="widget-image-container">
        <img 
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=200&q=80" 
          alt="Analytics Dashboard" 
          className="widget-image"
        />
        <div className="widget-overlay"></div>
      </div>
      <div className="widget-content">
        <div className="widget-header">
          <div className="widget-controls">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <div className="widget-stats">
          <div className="stat-item"></div>
          <div className="stat-item"></div>
        </div>
        <div className="widget-chart">
          <div className="chart-column"></div>
          <div className="chart-column"></div>
          <div className="chart-column"></div>
          <div className="chart-column"></div>
        </div>
      </div>
    </div>

    {/* Chat Widget with Image */}
    <div className="quantum-floating-widget widget-chat">
      <div className="widget-image-container">
        <img 
          src="https://images.unsplash.com/photo-1577563908411-5077b6dc7624?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&h=200&q=80" 
          alt="Team Collaboration" 
          className="widget-image"
        />
        <div className="widget-overlay"></div>
      </div>
      <div className="widget-content">
        <div className="widget-avatars">
          <div className="avatar-circle"></div>
          <div className="avatar-circle"></div>
          <div className="avatar-circle"></div>
          <div className="avatar-circle"></div>
        </div>
        <div className="widget-messages">
          <div className="message-line"></div>
          <div className="message-line"></div>
        </div>
        <div className="widget-input"></div>
      </div>
    </div>

  </div>
</div>
      </div>

      {/* Auth Modal */}
      {isModalOpen && (
        <div className="quantum-auth-modal">
          <div
            className="quantum-modal-backdrop"
            onClick={() => setIsModalOpen(false)}
          ></div>
          <div
            className="quantum-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="quantum-modal-close"
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>

            <div className="quantum-modal-header">
              <h2>{isLoginForm ? "Welcome Back" : "Create Your Account"}</h2>
              <p>
                {isLoginForm
                  ? "Sign in to continue your productivity journey"
                  : "Join thousands of teams boosting their productivity"}
              </p>
            </div>

            {/* Google Login */}
            <button
              className="quantum-google-auth-btn"
              onClick={handleGoogleLogin}
            >
              <svg className="quantum-google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="quantum-divider">
              <span>Or continue with email</span>
            </div>

            {/* OTP Forms */}
            {isLoginForm ? (
              <form onSubmit={handleLoginSubmit} className="quantum-auth-form">
                <div className="quantum-input-group">
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleLoginInputChange}
                    placeholder=" "
                    required
                    disabled={loginStep}
                    className="quantum-form-input"
                  />
                  <label className="quantum-input-label">Work Email</label>
                </div>
                {loginStep && (
                  <div className="quantum-input-group">
                    <input
                      type="text"
                      name="otp"
                      value={loginData.otp}
                      onChange={handleLoginInputChange}
                      placeholder=" "
                      required
                      className="quantum-form-input"
                    />
                    <label className="quantum-input-label">Enter OTP</label>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="quantum-submit-btn"
                >
                  {isSubmitting
                    ? loginStep
                      ? "Verifying..."
                      : "Sending OTP..."
                    : loginStep
                    ? "Verify OTP"
                    : "Send OTP"}
                </button>
                {loginStep &&
                  (resendTimer > 0 ? (
                    <p className="quantum-timer-text">
                      Resend OTP in {resendTimer}s
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="quantum-resend-link"
                      onClick={() => handleResendOtp("login")}
                    >
                      Resend OTP
                    </button>
                  ))}
              </form>
            ) : (
              <form onSubmit={handleSignupSubmit} className="quantum-auth-form">
                <div className="quantum-input-group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    className="quantum-form-input"
                  />
                  <label className="quantum-input-label">Full Name</label>
                </div>
                <div className="quantum-input-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    className="quantum-form-input"
                  />
                  <label className="quantum-input-label">Work Email</label>
                </div>
                <div className="quantum-input-group">
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    className="quantum-form-input"
                  />
                  <label className="quantum-input-label">Company</label>
                </div>
                <div className="quantum-input-group">
                  <select
                    name="employees"
                    value={formData.employees}
                    onChange={handleInputChange}
                    required
                    className="quantum-form-select"
                  >
                    <option value=""></option>
                    <option value="1-50">1-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                  <label className="quantum-input-label">Company Size</label>
                </div>
                <div className="quantum-input-group">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    className="quantum-form-input"
                  />
                  <label className="quantum-input-label">Password</label>
                </div>
                {signupStep && (
                  <div className="quantum-input-group">
                    <input
                      type="text"
                      name="otp"
                      value={signupOtp}
                      onChange={(e) => setSignupOtp(e.target.value)}
                      placeholder=" "
                      required
                      className="quantum-form-input"
                    />
                    <label className="quantum-input-label">Enter OTP</label>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="quantum-submit-btn"
                >
                  {isSubmitting
                    ? signupStep
                      ? "Verifying..."
                      : "Sending OTP..."
                    : signupStep
                    ? "Verify OTP"
                    : "Create Account"}
                </button>
                {signupStep &&
                  (resendTimer > 0 ? (
                    <p className="quantum-timer-text">
                      Resend OTP in {resendTimer}s
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="quantum-resend-link"
                      onClick={() => handleResendOtp("signup")}
                    >
                      Resend OTP
                    </button>
                  ))}
              </form>
            )}

            {message.text && (
              <div className={`quantum-message-banner ${message.type === "error" ? "quantum-error" : "quantum-success"}`}>
                {message.text}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSectionOnboarding;